<template>
  <main class="min-h-screen bg-white" v-if="info">

    <!-- Key Features -->
    <section class="hero-section relative py-16 md:py-24 h-[500px]" ref="heroSection" :style="{ backgroundImage: `url(${require(`@/assets/services.jpg`)})`, backgroundSize: 'cover', backgroundPosition: 'center' }">
      <!-- <div class="absolute inset-0 bg-black opacity-50"></div> -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="max-w-3xl">
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-10" :class="{ 'animate-slideInUp': heroVisible }">
          </h1>
        </div>
      </div>
    </section>

    <!-- Description -->
    <section class="pt-10" ref="descriptionSection">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p class="text-gray-700 text-lg leading-relaxed mb-4 text-justify" :class="{ 'animate-slideInUp': descriptionVisible }" v-html="info.desc"></p>
      </div>
    </section>

    <!-- Process Steps -->
    <section class="pb-10" ref="stepsSection">
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mt-10">
        <div 
          v-for="(item, index) in info.steps" 
          :key="'item'+index"
          class="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-red-200 transition-all duration-300" 
          :class="{ 'animate-slideInUp': featuresVisible }" 
          :style="featuresVisible ? { 'animation-delay': (index * 0.1 + 0.3) + 's' } : {}"
        >
          <div class="relative">
            <video v-if="(index+1) == 5" class="w-full h-48 object-cover" autoplay muted loop playsinline>
              <source :src="require(`@/assets/ss${index+1}.mp4`)" type="video/mp4">
            </video>
            <img :src="require(`@/assets/ss${index+1}.jpg`)" class="w-full h-48 object-cover" v-else />
            <div class="absolute bottom-0 left-0 w-full bg-opacity-50 p-4">
              <h3 class="text-white text-lg sm:text-xl font-bold">{{ item.title }}</h3>
            </div>
          </div>
          <p class="p-4 text-sm sm:text-base text-left">{{ item.desc }}</p>
        </div>
      </div>
    </section>

    <!-- Service Categories -->
    <section class="py-10" ref="featuresSection" :style="{ backgroundImage: `url(${require(`@/assets/services2.jpg`)})`, backgroundSize: 'cover', backgroundPosition: 'center' }">
      <!-- <div class="absolute inset-0 bg-black opacity-20"></div> -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="(cat, index) in info.list" :key="index" class="p-6 bg-black bg-opacity-30 text-white border rounded-lg shadow-sm" :class="{ 'animate-slideInUp': featuresVisible }" :style="featuresVisible ? { 'animation-delay': (index * 0.1) + 's' } : {}">
            <h4 class="text-2xl font-bold mb-4">{{ cat.section }}</h4>
            <ul class="list-disc text-md list-inside space-y-2">
              <li v-for="(item, i) in cat.items" :key="i">{{ item.name }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import homeData from '@/data/home.json'
export default {
  name: 'MainServicePage',
  data() {
    return {
      data: homeData,
      info: null,
      heroVisible: false,
      descriptionVisible: false,
      stepsVisible: false,
      featuresVisible: false,
    }
  },
  mounted() {
    this.info = this.data.main_services
    // Show hero immediately
    this.heroVisible = true
    // Set up observer for other sections
    this.$nextTick(() => {
      this.setupSectionObserver()
    })
  },
  beforeUnmount() {
    if (this.sectionObserver) {
      this.sectionObserver.disconnect()
    }
  },
  methods: {
    setupSectionObserver() {
      if (!('IntersectionObserver' in window)) {
        this.heroVisible = true
        this.descriptionVisible = true
        this.featuresVisible = true
        return
      }
      
      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const ref = entry.target.getAttribute('data-section')
              if (ref === 'hero') this.heroVisible = true
              if (ref === 'description') this.descriptionVisible = true
              if (ref === 'steps') this.stepsVisible = true
              if (ref === 'features') this.featuresVisible = true
            }
          })
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        }
      )
      
      if (this.$refs.heroSection) {
        this.$refs.heroSection.setAttribute('data-section', 'hero')
        this.sectionObserver.observe(this.$refs.heroSection)
      }
      if (this.$refs.descriptionSection) {
        this.$refs.descriptionSection.setAttribute('data-section', 'description')
        this.sectionObserver.observe(this.$refs.descriptionSection)
      }
      if (this.$refs.stepsSection) {
        this.$refs.stepsSection.setAttribute('data-section', 'steps')
        this.sectionObserver.observe(this.$refs.stepsSection)
      }
      if (this.$refs.featuresSection) {
        this.$refs.featuresSection.setAttribute('data-section', 'features')
        this.sectionObserver.observe(this.$refs.featuresSection)
      }
    }
  }
}
</script>

<style scoped>
.prose {
  color: #374151;
}
.prose p {
  margin-bottom: 2rem;
}

/* Fade In Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-fadeIn {
  animation: fadeIn 0.8s ease-in-out both;
}

/* Fade In Up Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.8s ease-out both;
}

/* Slide In Up Animation */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slideInUp {
  animation: slideInUp 0.8s ease-out both;
}
</style>